import React, { useEffect, useRef, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import useImageUpload from '../../hooks/useImageUpload';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { callApi } from '../../api/callApi';
import { API_ENDPOINT, API_URL_2 } from '../../api/constant';
import { showToastNotification } from '../../utility/helper';
import InputField from '../../components/UI/Forms/InputField';
import ErrorMessage from '../../components/UI/Notifications/ErrorMessage';
import Button from '../../components/UI/Buttons/Button';
import SelectDropdown from '../../components/UI/Forms/SelectDropdown';
import { useNavigate, useParams } from 'react-router-dom';
import { ICategory } from '../../interfaces/category';
import Textarea from '../../components/UI/Forms/Textarea';
import { IProduct } from '../../interfaces/product';
import ModelViewer from '../../components/common/ModalView3D';
import useFileDelete from '../../hooks/useFileDelete';
import SeoForm, { SeoFormRef, submitSEOData } from '../../components/common/SEOForm';
import { SeoFormData } from '../../interfaces/seo';

const AddProduct: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { uploadImage, isUploading, error } = useImageUpload();
    const { deleteFile } = useFileDelete();
    const [categories, setCategories] = useState<any[]>([]);
    const [subCategoryData, setSubCategoryData] = useState<ICategory[]>([]);
    const [subCategories, setSubCategories] = useState<{ value: string, label: string }[]>([]);
    const [brands, setBrands] = useState<{ value: string, label: string }[]>([]);
    const [origin, setOrigin] = useState<{ value: string, label: string }[]>([]);
    const [units, setUnits] = useState<{ value: string, label: string }[]>([]);
    const [grades, setGrades] = useState<{ value: string, label: string }[]>([]);
    const [valueAdditions, setValueAdditions] = useState<{ value: string, label: string }[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedCategory, setSelectedCategory] = useState<ICategory>();
    const [ARImage, setARImage] = useState<string>()
    const seoFormRef = useRef<SeoFormRef>(null);
    const [seoData, setSeoData] = useState<SeoFormData>({
        entityType: 'product',
    });
    const [isSlugTouched, setIsSlugTouched] = useState(false);
    const { register, handleSubmit, formState: { errors }, reset, setValue, watch, control, getValues } = useForm({
        mode: 'onBlur',
    });
    const { fields: productPriceInfo, append: appendProductPriceInfo, remove: removeProductPriceInfo } = useFieldArray({
        control,
        name: 'productPriceInfo',
    });
    const [imageURL, setImageURl] = useState<string[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        if (productPriceInfo.length === 0) {
            appendProductPriceInfo([{ price: '', quantity: '', pieceWeight: '', isVatIncluded: true }]);
        }
        fetchDropdownData();
    }, []);

    useEffect(() => {
        if (categories && id) {
            fetchProductDetails();
        }
    }, [categories]);

    const state = watch('state');
    const origin_watch = watch('origin');
    const brand = watch('brand');
    const typeGrade = watch('typeGrade');
    const categoryId = categories.find((cat) => cat.value === watch('categoryId') || '')?.label;
    const cut = watch('cut');
    const valueAddition = watch('valueAddition');
    const titleValue = useWatch({
        control,
        name: 'title',
    });
    useEffect(() => {
        if (titleValue && !isSlugTouched) {
            const generatedSlug = generateSlug(titleValue);
            setValue('slug', generatedSlug);
        }
    }, [titleValue, setValue, control, isSlugTouched]);

    const generateSlug = (text: string) => {
        return text
            .toLowerCase()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9-]/g, '')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
    };


    const handleSlugFocus = () => {
        setIsSlugTouched(true);
    };
    const makeSingular = (word: string) => {
        if (!word) return word;

        // Handle simple plurals ending in "s"
        if (word.toLowerCase().endsWith('s') && word.toLowerCase().length > 1 && !word.toLowerCase().endsWith('ss')) {
            return word.slice(0, -1); // Remove the trailing 's'
        }

        return word; // Return as-is if no changes
    };
    useEffect(() => {
        if (!id) {
            let finalCut = makeSingular(cut);
            let finalValueAddition = makeSingular(valueAddition);

            if (finalCut && finalValueAddition && finalCut.includes(finalValueAddition)) {
                finalValueAddition = ''; // Avoid adding duplicate valueAddition
            }

            // Remove redundancy between `cut` and `valueAddition`
            if (cut && valueAddition && cut.includes(valueAddition)) {
                finalValueAddition = ''; // Avoid adding duplicate valueAddition
            }

            // Concatenate values dynamically
            const combinedTitle = [
                state,
                origin_watch,
                brand,
                typeGrade,
                categoryId,
                finalCut,
                finalValueAddition,
            ].filter(Boolean)
                .join(' ');

            setValue('title', combinedTitle);
        }
    }, [state, origin_watch, brand, typeGrade, categoryId, cut, valueAddition, setValue]);


    const fetchDropdownData = async () => {
        try {
            const [categoriesRes, brandsRes, gradesRes, valueAdditionsRes, unitRes, originRes] = await Promise.all([
                callApi(API_URL_2 + API_ENDPOINT.page + 'categories', { method: 'GET' }),
                callApi(API_URL_2 + API_ENDPOINT.page + 'brand', { method: 'GET' }),
                callApi(API_URL_2 + API_ENDPOINT.page + 'grade', { method: 'GET' }),
                callApi(API_URL_2 + API_ENDPOINT.page + 'valueAddition', { method: 'GET' }),
                callApi(API_URL_2 + API_ENDPOINT.page + 'unit', { method: 'GET' }),
                callApi(API_URL_2 + API_ENDPOINT.page + 'origin', { method: 'GET' }),
            ]);

            setCategories(categoriesRes.filter((cat1: any) => cat1.type === 'primary').map((cat: any) => ({ ...cat, value: cat.id, label: cat.title })));
            setSubCategoryData(categoriesRes.filter((cat1: ICategory) => cat1.type === 'sub'));
            setBrands(brandsRes.map((brand: any) => ({ value: brand.title, label: brand.title })));
            setUnits(unitRes.map((brand: any) => ({ value: brand.title, label: brand.title })));
            setOrigin(originRes.map((brand: any) => ({ value: brand.title, label: brand.title })));
            setGrades(gradesRes.map((grade: any) => ({ value: grade.title, label: grade.title })));
            setValueAdditions(valueAdditionsRes.map((val: any) => ({ value: val.title, label: val.title })));
        } catch (error: any) {
            console.error('Failed to fetch dropdown data:', error.message);
        }
    };

    const fetchProductDetails = async () => {
        try {
            const product: IProduct = await callApi(API_URL_2 + API_ENDPOINT.page + `products/${id}`, { method: 'GET' });
            handleCategoryChange(categories.find((cat) => cat.label === product.typeOfMeat[0])?.value || '');
            setValue('productCode', product.productCode);
            setValue('title', product.title);
            setValue('slug', product.slug);
            if (product.slug) { setIsSlugTouched(true); }
            setValue('additionalInfo', product?.additionalInfo || '');
            setValue('description', product?.description || '');
            setValue('isRecommended', product.isRecommended);
            setValue('categoryId', categories.find((cat) => cat.label === product.typeOfMeat[0])?.value || '');
            setValue('state', product.state);
            setValue('origin', product.origin);
            setValue('brand', product.brand);
            setValue('typeGrade', product.typeGrade);
            setValue('3dImage', product['3dImage'])
            setTimeout(() => {
                setValue('cut', product.cut);
            }, 500);
            const productPriceInfoWithId = product.productInfo.map((info: any) => ({
                price: info.price,
                productId: info.productId,
                productInfoId: info.id,
                quantity: info.quantity,
                pieceWeight: info.pieceWeight,
                isVatIncluded: info.isVatIncluded
            }));

            setValue("productPriceInfo", productPriceInfoWithId);
            setValue('valueAddition', product.valueAddition);
            setImageURl(product.images || []);
            setValue('images', product.images || []);
            setARImage(product['3dImage']);
            if (product.seo) {
                setSeoData(product.seo);
            }
        } catch (error: any) {
            console.error('Failed to fetch product details:', error.message);
        }
    };

    const onSubmit = async (data: any) => {
        // setLoading(true);
        const postData = {
            ...data,
            typeOfMeat: [categories.find((cat) => cat.value === data.categoryId)?.label],
            images: imageURL,
            subcategoryId: subCategoryData.find((cat) => cat.title === data.cut && cat.categoryId === data.categoryId)?.id,
            ['3dImage']: ARImage ? data['3dImage'] : null
        };
        let productId = id || '';
        try {
            if (id) {
                await callApi(API_URL_2 + API_ENDPOINT.page + `products/${id}`, { method: 'PUT', data: postData });
                showToastNotification('success', 'Product updated successfully');
            } else {
                const res = await callApi(API_URL_2 + API_ENDPOINT.page + 'products', { method: 'POST', data: postData });
                productId = res.id
                showToastNotification('success', 'Product added successfully');
            }
            if (seoFormRef.current) {
                const isValid = await seoFormRef.current.triggerValidation();
                const seoFormData = seoFormRef.current.getFormData();
                console.log('SEO Data:', seoData, 'Is Valid:', isValid);
                if (isValid) {
                    let newSeoData = {
                        ...seoFormData,
                        entityId: productId,
                        entityType: 'product',
                    }
                    submitSEOData(newSeoData, seoData.id);
                } else {
                    showToastNotification('danger', 'SEO form has validation errors');
                    // You can focus on the first error or show a message
                }
            }
            reset();
            navigate('/products');
        } catch (error: any) {
            showToastNotification('danger', error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, type: string) => {
        const files = event.target.files;
        if (!files) return;

        const uploadedURLs: string[] = [];
        for (const file of Array.from(files)) {
            if (type === 'image' && file.size > 1 * 1024 * 1024) {
                showToastNotification('danger', `"${file.name}" exceeds 1MB limit and was skipped.`);
                continue;
            }
            try {
                const uploadedUrl = await uploadImage(file);
                uploadedURLs.push(uploadedUrl);
            } catch (err) {
                console.error(err);
            }
        }
        if (type === 'AR') {
            setARImage(uploadedURLs[0]);
            setValue('3dImage', uploadedURLs[0])
        } else {
            setImageURl((prev) => [...prev, ...uploadedURLs]);
            setValue('images', [...imageURL, ...uploadedURLs]);
        }
    };

    const handleDeleteImage = async (url: string) => {
        setImageURl((prev) => prev.filter((image) => image !== url));
        setValue('images', imageURL.filter((image) => image !== url));
        await deleteFile(url);
    };

    const handleCategoryChange = (selectedCategoryId: string) => {
        setSelectedCategory(categories.find(cat => cat.id === selectedCategoryId))
        const filteredSubCategories = subCategoryData.filter(
            (subCat) => subCat.categoryId === selectedCategoryId
        ).map((cat) => { return { value: cat.title, label: cat.title } });
        setSubCategories(filteredSubCategories);
    };

    const handleDeleteProductInfo = async (index: number) => {
        const selectedProduct: any = productPriceInfo[index];
        try {
            if (id && selectedProduct?.productInfoId) {
                await callApi(API_URL_2 + API_ENDPOINT.page + `products/delete-info/${selectedProduct.productInfoId}`, { method: 'DELETE', });
                removeProductPriceInfo(index)
            } else {
                removeProductPriceInfo(index)
            }

        } catch (error: any) {
            showToastNotification('danger', error.message);
        }
    }

    // Extract numeric weight from pieceWeight
    const extractWeight = (product: any): any => {
        return product.pieceWeight.replace(/[^0-9]/g, ""); // Remove all non-numeric characters
    };

    // Extract unit (GRAMS or KG) from pieceWeight
    const extractUnit = (product: any): any => {
        const match = product.pieceWeight.match(/[A-Za-z]+/g); // Extract alphabetic part
        return match ? match[0].toUpperCase() : "GRAMS"; // Default to GRAMS if not found
    };

    const isVatCheckboxDisabled = (index: number) => {
        if (!id) {
            // Add page - always disabled (cannot uncheck)
            return true;
        } else {
            // Edit page - disabled if currently checked
            const currentValue = getValues(`productPriceInfo.${index}.isVatIncluded`);
            return currentValue === true;
        }
    };

    React.useEffect(() => {
        if (!id && productPriceInfo.length > 0) {
            productPriceInfo.forEach((_, index) => {
                setValue(`productPriceInfo.${index}.isVatIncluded`, true);
            });
        }
    }, [id, productPriceInfo.length]);
    return (
        <Layout>
            <h1 className="app-page-title">{id ? 'Edit Product' : 'Add Product'}</h1>
            <div className="app-card app-card-settings shadow-sm p-4">
                <div className="app-card-body">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="row">
                            {/* productCode */}
                            <div className="mb-3 col-lg-4 col-md-6 col-12">
                                <InputField
                                    type="number"
                                    register={register('productCode', { required: 'Product Code is required' })}
                                    name="productCode"
                                    label="Product Code"
                                    placeholder="Enter product code"
                                />
                                <ErrorMessage message={errors.productCode?.message?.toString()} />
                            </div>
                            {/* Type of Meat */}
                            <div className="mb-3 col-lg-4 col-md-6 col-12">
                                <SelectDropdown
                                    register={register('categoryId', {
                                        required: 'Type of Meat is required',
                                        onChange: (e: React.ChangeEvent<HTMLSelectElement>) =>
                                            handleCategoryChange(e.target.value), // Call the handler on category change
                                    })}
                                    name="categoryId"
                                    label="Type of Meat"
                                    options={categories}
                                />
                                <ErrorMessage message={errors.categoryId?.message?.toString()} />
                            </div>

                            {selectedCategory?.cut === true && <div className="mb-3 col-lg-4 col-md-6 col-12">
                                <SelectDropdown
                                    register={register('cut', { required: false })}
                                    name="cut"
                                    label="Cut"
                                    options={subCategories}
                                />
                                <ErrorMessage message={errors.cut?.message?.toString()} />
                            </div>}
                            {selectedCategory?.state === true && <div className="mb-3 col-lg-4 col-md-6 col-12">
                                <SelectDropdown
                                    register={register('state', { required: 'State is required' })}
                                    name="state"
                                    label="State"
                                    options={[
                                        { value: 'CHILLED', label: 'CHILLED' },
                                        { value: 'FROZEN', label: 'FROZEN' },
                                        { value: 'AMBIENT', label: 'AMBIENT' },
                                        { value: 'FRESH', label: 'FRESH' },
                                    ]}
                                />
                                <ErrorMessage message={errors.state?.message?.toString()} />
                            </div>}

                            {selectedCategory?.origin === true && <div className="mb-3 col-lg-4 col-md-6 col-12">
                                <SelectDropdown
                                    register={register('origin', { required: 'Origin is required' })}
                                    name="origin"
                                    label="Origin"
                                    options={origin}
                                />
                                <ErrorMessage message={errors.origin?.message?.toString()} />
                            </div>}

                            {selectedCategory?.brand && <div className="mb-3 col-lg-4 col-md-6 col-12">
                                <SelectDropdown
                                    register={register('brand', { required: false })}
                                    name="brand"
                                    label="Brand"
                                    options={brands}
                                />
                                <ErrorMessage message={errors.brand?.message?.toString()} />
                            </div>}

                            {selectedCategory?.grade && <div className="mb-3 col-lg-4 col-md-6 col-12">
                                <SelectDropdown
                                    register={register('typeGrade', { required: false })}
                                    name="typeGrade"
                                    label="Type/Grade"
                                    options={grades}
                                />
                                <ErrorMessage message={errors.typeGrade?.message?.toString()} />
                            </div>}

                            {selectedCategory?.valueAddition && <div className="mb-3 col-lg-4 col-md-6 col-12">
                                <SelectDropdown
                                    register={register('valueAddition', { required: false })}
                                    name="valueAddition"
                                    label="Savory Essential"
                                    options={valueAdditions}
                                />
                                <ErrorMessage message={errors.valueAddition?.message?.toString()} />
                            </div>
                            }

                            <div className="mb-3 col-lg-12 col-md-12 col-12">
                                <InputField
                                    type="text"
                                    register={register('title', { required: 'Title is required' })}
                                    name="title"
                                    label="Product Name"
                                    placeholder="Enter title"
                                />
                                <ErrorMessage message={errors.title?.message?.toString()} />
                            </div>
                            <div className='mb-3 col-lg-12 col-md-12 col-12'>
                                <label htmlFor="slug" className="form-label">Slug</label>
                                <input
                                    type="text"
                                    {...register('slug', {
                                        required: 'Slug is required',
                                        pattern: {
                                            value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                                            message: 'Slug must be URL-friendly: lowercase letters, numbers, hyphens. No spaces or special characters.'
                                        }
                                    })}
                                    className='form-control'
                                    name="slug"
                                    placeholder="Enter slug"
                                    onFocus={handleSlugFocus}
                                    onChange={() => setIsSlugTouched(true)}
                                />
                                <ErrorMessage message={errors.slug?.message?.toString()} />
                            </div>

                            <div className="mb-3 col-12 col-md-6">
                                <Textarea
                                    register={register('description', { required: false })}
                                    name="description"
                                    label="Description"
                                    placeholder="Enter description"
                                />
                                <ErrorMessage message={errors.description?.message?.toString()} />
                            </div>

                            <div className="mb-3 col-12 col-md-6">
                                <Textarea
                                    register={register('additionalInfo', { required: false })}
                                    name="additionalInfo"
                                    label="Additional Info"
                                    placeholder="Enter Additional Info"
                                />
                                <ErrorMessage message={errors.additionalInfo?.message?.toString()} />
                            </div>

                            {/* Price */}

                            <div className="mb-3 col-12 mt-5">
                                <div className='mb-3'>
                                    <h5 className='mb-3 fw-bold'>Product Pricing Info</h5>
                                    {productPriceInfo.map((field, index) => {
                                        const isDisabled = isVatCheckboxDisabled(index);
                                        return (
                                            <div key={field.id} className="instruction-row mb-3 row">
                                                <div className="col-11">
                                                    <div className="row">
                                                        {/* Price Field */}
                                                        <div className="mb-3 col-md-3 col-12">
                                                            <InputField
                                                                type="number"
                                                                register={register(`productPriceInfo.${index}.price`, {
                                                                    required: 'Price is required'
                                                                })}
                                                                name={`productPriceInfo.${index}.price`}
                                                                label="Price"
                                                                placeholder="Enter price"
                                                                step="any"
                                                            />
                                                            <ErrorMessage
                                                                message={
                                                                    Array.isArray(errors?.productPriceInfo) &&
                                                                    errors.productPriceInfo[index]?.price?.message
                                                                }
                                                            />
                                                        </div>

                                                        {/* Quantity Field */}
                                                        <div className="mb-3 col-md-3 col-12">
                                                            <InputField
                                                                type="number"
                                                                register={register(`productPriceInfo.${index}.quantity`, {
                                                                    required: 'Available Stock is required'
                                                                })}
                                                                name={`productPriceInfo.${index}.quantity`}
                                                                label="Available Stock"
                                                                placeholder="Enter quantity"
                                                            />
                                                            <ErrorMessage
                                                                message={
                                                                    Array.isArray(errors?.productPriceInfo) &&
                                                                    errors.productPriceInfo[index]?.quantity?.message
                                                                }
                                                            />
                                                        </div>
                                                        {/* Piece Weight Field */}
                                                        <div className="mb-3 col-md-4 col-12">
                                                            <label htmlFor={`productPriceInfo.${index}.pieceWeight`} className="form-label">
                                                                Piece Weight
                                                            </label>
                                                            <div className="input-group">
                                                                {/* Numeric Input */}
                                                                <input
                                                                    type="text"
                                                                    {...register(`productPriceInfo.${index}.weight`, {
                                                                        required: "Weight is required",
                                                                        validate: (value) =>
                                                                            /^(\d+(\.\d+)?)(\*(\d+(\.\d+)?))*$/.test(value) || "Only numbers or dimension formats like 30*30 or 30*30*30 are allowed"
                                                                    })}
                                                                    defaultValue={extractWeight(productPriceInfo && productPriceInfo[index])}
                                                                    placeholder="Enter weight"
                                                                    className="form-control"
                                                                    onChange={(e) => {
                                                                        const weight = e.target.value;
                                                                        const unit = getValues(`productPriceInfo.${index}.unit`) || "GRAMS"; // Default unit
                                                                        setValue(`productPriceInfo.${index}.pieceWeight`, `${weight} ${unit}`);
                                                                    }}
                                                                />
                                                                {/* Unit Select */}
                                                                <select
                                                                    className="form-select"
                                                                    {...register(`productPriceInfo.${index}.unit`, {
                                                                        required: true,
                                                                    })}
                                                                    defaultValue={extractUnit(productPriceInfo[index])}
                                                                    onChange={(e) => {
                                                                        const weight = getValues(`productPriceInfo.${index}.weight`) || ""; // Current weight
                                                                        const unit = e.target.value;
                                                                        setValue(`productPriceInfo.${index}.pieceWeight`, `${weight} ${unit}`);
                                                                    }}
                                                                >
                                                                    <option value="">Select Option</option>
                                                                    {units && units.length > 0 &&
                                                                        units.map((unit, index) => <option key={index} value={unit.label}>{unit.label}</option>)}
                                                                </select>
                                                            </div>
                                                            <ErrorMessage
                                                                message={
                                                                    Array.isArray(errors?.productPriceInfo) &&
                                                                    errors.productPriceInfo[index]?.weight?.message
                                                                }
                                                            />
                                                            <ErrorMessage
                                                                message={
                                                                    Array.isArray(errors?.productPriceInfo) &&
                                                                    errors.productPriceInfo[index]?.pieceWeight?.message
                                                                }
                                                            />
                                                        </div>
                                                        <div className='mb-3 col-md-2 col-12'>

                                                            <div className="form-check mt-4 pt-3">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    {...register(`productPriceInfo.${index}.isVatIncluded`, {
                                                                        required: false
                                                                    })}
                                                                    id={"flexCheckDefault" + index}
                                                                    disabled={isDisabled}
                                                                    onChange={(e) => {
                                                                        if (isDisabled && !e.target.checked) {
                                                                            e.preventDefault();
                                                                            return;
                                                                        }
                                                                    }}
                                                                />
                                                                <label className="form-check-label" htmlFor={"flexCheckDefault" + index}>
                                                                    VAT Included ?
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>
                                                <div className="col-1">
                                                    <div className='mb-3 '>
                                                        <div className='form-label opacity-0'>a</div>
                                                        {/* Remove Button */}
                                                        {index !== 0 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger"
                                                                onClick={() => handleDeleteProductInfo(index)}
                                                            >
                                                                <i className='bi bi-trash3'></i>
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    })}

                                </div>
                                <button type="button" className='btn app-btn-primary d-flex align-items-center gap-2' onClick={() => appendProductPriceInfo({ price: '', quantity: '', pieceWeight: '' })}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M6 0C6.13261 0 6.25979 0.0526785 6.35355 0.146447C6.44732 0.240215 6.5 0.367392 6.5 0.5V5.5H11.5C11.6326 5.5 11.7598 5.55268 11.8536 5.64645C11.9473 5.74021 12 5.86739 12 6C12 6.13261 11.9473 6.25979 11.8536 6.35355C11.7598 6.44732 11.6326 6.5 11.5 6.5H6.5V11.5C6.5 11.6326 6.44732 11.7598 6.35355 11.8536C6.25979 11.9473 6.13261 12 6 12C5.86739 12 5.74021 11.9473 5.64645 11.8536C5.55268 11.7598 5.5 11.6326 5.5 11.5V6.5H0.5C0.367392 6.5 0.240215 6.44732 0.146447 6.35355C0.0526785 6.25979 0 6.13261 0 6C0 5.86739 0.0526785 5.74021 0.146447 5.64645C0.240215 5.55268 0.367392 5.5 0.5 5.5H5.5V0.5C5.5 0.367392 5.55268 0.240215 5.64645 0.146447C5.74021 0.0526785 5.86739 0 6 0Z" fill="white"></path></svg>  Add More Product Pricing Info
                                </button>
                            </div>

                            <div className="mb-5 col-12">
                                <input
                                    className='form-check-input'
                                    type="checkbox"
                                    {...register('isRecommended')}
                                />
                                <label className='form-check-label ms-2'>
                                    Is Recommended
                                </label>
                            </div>


                            <div className="mb-3 col-12">
                                <div className='form-label'>Add product image</div>
                                <div className="row">
                                    <div className="col-md-3">
                                        <label htmlFor="file-input" className="choosefilenew position-relative">
                                            <input
                                                type="file"
                                                className='form-control'
                                                id="file-input"
                                                accept="image/*"
                                                hidden
                                                {...register('images', {
                                                    validate: () => (imageURL.length > 0 ? true : 'At least one image is required'),
                                                })}
                                                onChange={(e) => handleFileChange(e, 'image')}
                                            />

                                            <div>
                                                <div><div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>{isUploading ? 'Uploading' : 'Upload Image'}</div>
                                            </div>
                                        </label>
                                    </div>
                                    <div className="col-md-9">
                                        <div className='addimagefiles row'>
                                            {/* Display uploaded images */}
                                            {imageURL.map((url, index) => (
                                                <div className='col-md-3 mb-2' key={index}>
                                                    <div className="image-card position-relative">
                                                        <img src={url} alt={`Uploaded ${index}`} className="image-preview" />
                                                        {/* Move the delete button OUTSIDE the label */}
                                                        <button
                                                            className='btn btn-danger px-2 py-1 text-white position-absolute'
                                                            type="button"
                                                            onClick={() => handleDeleteImage(url)}
                                                        >
                                                            <i className='bi bi-trash3'></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <ErrorMessage message={errors.image?.message?.toString()} />
                                <ErrorMessage message={error?.toString()} />
                            </div>
                            <div className="mb-3 col-12">
                                <div className='form-label'>Add 3D Model</div>
                                <div className="row">
                                    <div className="col-md-3">
                                        <label htmlFor="file-input-123" className="choosefilenew position-relative">
                                            <input
                                                type="file"
                                                className='form-control'
                                                id="file-input-123"
                                                accept="glb/*"
                                                hidden
                                                multiple={false}
                                                {...register('3dImage')}
                                                onChange={(e) => handleFileChange(e, "AR")}
                                            />

                                            <div>
                                                <div><div className='mb-2'><i className="bi fs-4 bi-upload"></i></div>{isUploading ? 'Uploading' : ' Upload 3d Image (.GLB)'}</div>
                                            </div>
                                        </label>
                                    </div>
                                    {ARImage && <div className="col-md-9">
                                        <div className='addimagefiles row'>
                                            {/* Display uploaded images */}
                                            <div className='col-md-6 mb-2' >
                                                <div className="image-card position-relative">
                                                    <ModelViewer modelUrl={ARImage} width="100%" height="200px" />
                                                    {/* Move the delete button OUTSIDE the label */}
                                                    <button
                                                        className='btn btn-danger px-2 py-1 text-white position-absolute'
                                                        type="button"
                                                        onClick={() => setARImage('')}
                                                    >
                                                        <i className='bi bi-trash3'></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>}
                                </div>

                                <ErrorMessage message={errors.image?.message?.toString()} />
                                <ErrorMessage message={error?.toString()} />
                            </div>
                            <div className="mb-3 col-12">
                                <SeoForm
                                    ref={seoFormRef}
                                    entityId={id || ''}
                                    entityType="product"
                                    initialData={seoData}
                                    isRequired={false}
                                />
                            </div>
                            {/* Submit Button */}
                            <div className="col-12 text-end mt-3">
                                <Button loading={loading} className="btn btn-primary text-white px-5" type="submit" text="Save" />
                            </div>
                        </div>
                    </form>
                </div>
            </div>

        </Layout>
    );
};

export default AddProduct;
